import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../../core/_internal/factory.js';
import { CloseButton } from '../../core/CloseButton/CloseButton.js';
import './Alert.css';

export type AlertVariant = 'light' | 'filled' | 'outline' | 'glass';
export type AlertColor = 'info' | 'success' | 'warning' | 'error' | string;

export interface AlertProps extends BaseProps {
  title?: ReactNode;
  children?: ReactNode;
  /** Status color: mapped to `--rr-color-{info|success|warning|error}` or a raw value. */
  color?: AlertColor;
  /** Visual style: light tint (default), filled, outline, or frosted glass. */
  variant?: AlertVariant;
  /** Backdrop blur in px (used with `variant='glass'`). Default 10. */
  blur?: number;
  /** Icon shown on the left of the title. */
  icon?: ReactNode;
  /** Show a × button that fires `onClose`. */
  withCloseButton?: boolean;
  onClose?: () => void;
}

/** Status / info banner: uses semantic colors from sdk-utils. */
export const Alert = forwardRef(function Alert(
  { title, children, color = 'info', variant = 'light', blur = 10, icon, withCloseButton, onClose, ...rest }: AlertProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const base = useBaseAttrs('rr-alert', rest, [
    `rr-alert--${variant}`,
    `rr-alert--color-${color}`
  ]);
  const { dom } = splitBaseProps(rest);
  const glassStyle = variant === 'glass'
    ? {
        backdropFilter: `blur(${blur}px) saturate(140%)`,
        WebkitBackdropFilter: `blur(${blur}px) saturate(140%)`
      }
    : undefined;
  return (
    <div {...dom}
      ref={ref}
      role="alert"
      {...base.attrs}
      className={base.className}
      style={{ ...base.style, ...glassStyle }}
    >
      {icon ? <span className="rr-alert__icon">{icon}</span> : null}
      <div className="rr-alert__body">
        {title ? <div className="rr-alert__title">{title}</div> : null}
        {children ? <div className="rr-alert__message">{children}</div> : null}
      </div>
      {withCloseButton ? <CloseButton onClick={onClose} className="rr-alert__close" /> : null}
    </div>
  );
});
